/*
 * @author HOPS
 * @copyright Heritage Operations Processing Limited
 */

import {useRef} from "react";
import {Button} from "Components/Button.js";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/DeleteOutlined";
import {FlexContainer} from "Components/FlexContainer.js";
import {Loader} from "Components/Loader.js";
import {Table} from "ComponentsE/table/Table";
import styles from "./TableList.module.scss";
import {parseLineBreaks} from "Helpers/HTMLParser";
import {Typography} from "./Typography";

/**
 * @param {String} title 
 * @param {Boolean} loading 
 * @param {Boolean} error 
 * @param {Object[]} data 
 * @param {Function} onEdit 
 * @param {Function} onDelete 
 * @param {String} labelsWidth 
 * @param {Boolean} hideEmpty return null if filtered data is empty
 * @param {String} emptyMessage 
 * @param {String} className 
 * @param {?String} variant // side, null/undefined (none)
 */
export const TableList = ({
	title,
	loading,
	error,
	data,
	onEdit,
	onDelete,
	labelsWidth,
	hideEmpty,
	emptyMessage,
	className,
	variant="side"
}) => {
	const visibleData = (data || []).filter(d => !d.hidden);

	const tableRef = useRef();

	/**
	 * @param {Mixed} content
	 */
	const renderContent = content => {
		if (loading || error) {
			return (
				<>
					{loading && <Loader disableCentre={true} size="small" />}
					{error && !loading && <Typography>Error</Typography>}
				</>
			);
		}
		else if (typeof content === "function") {
			content = content();
		}

		if (typeof content === "string") {
			content = parseLineBreaks(content);

		}

		return content;
	};

	/**
	 * Render a cell label.
	 *
	 * @param {Object} data
	 * @return {ReactNode}
	 */
	const renderLabel = data => {
		const labelWidth = labelsWidth || "24rem";

		const styles = {
			width: labelWidth,
			minWidth: labelWidth
		};

		return (
			<th style={styles}>
				<FlexContainer gap={0.5}>
					<p className={styles.label}>
						{data.label}
					</p>

					{
						data.caption ?
							<Typography color="textSecondary">
								{data.caption}
							</Typography> :
							null
					}
				</FlexContainer>
			</th>
		);
	};

	const handleEditClick = () => {
		if (onEdit) {
			onEdit();
		}
	};

	const handleDeleteClick = () => {
		if (onDelete) {
			onDelete();
		}
	};

	if (!visibleData.length && hideEmpty) {
		return null;
	}

	const classes = [];

	switch (variant) {
		case "side":
			classes.push("table-sidedisplaytable");
			break;
		default:
			break;
	}

	classes.push(styles.tableList);

	if (className) {
		classes.push(className);
	}

	return (
		<FlexContainer>
			{(title || onEdit || onDelete) ?
				<FlexContainer columnar={true} justifyContent="space-between">
					<Typography variant="h3">
						{title}
					</Typography>

					<FlexContainer
						columnar={true}
						gap={0.5}
						wrap={true}
						className={styles.actionButtons}>
						{onEdit ?
							<Button
								disabled={loading || error}
								label="Edit"
								onClick={handleEditClick}
								startIcon={<EditIcon />} /> :
							null
						}

						{onDelete ?
							<Button
								disabled={loading || error}
								label="Delete"
								onClick={handleDeleteClick}
								startIcon={<DeleteIcon />}
								variant="danger" /> :
							null
						}
					</FlexContainer>
				</FlexContainer> :
				null
			}

			{visibleData?.length ?
				<Table
					innerRef={tableRef}
					className={classes.join(" ")}
					variant={null}>
					<tbody>
						{visibleData.map((data, idx) => (
							<tr key={idx}>
								{data.label ? renderLabel(data) : null}

								<td colSpan={(data.label === undefined) ? 2 : null} style={data.style}>
									<FlexContainer gap={0.5}>
										{renderContent(data.content)}
									</FlexContainer>
								</td>
							</tr>
						))}
					</tbody>
				</Table> :
				<Typography>{emptyMessage || "Nothing to show."}</Typography>
			}
		</FlexContainer>
	);
};
